import Vue from 'vue'
import axios from 'axios'
import router from '@/router'
import qs from 'qs'
import { checkRouteIsNoNeedToLogin, checkIsLoginValid } from '../js/utils'
import i18n from '@/lang/i18n.js'
import * as Sentry from '@sentry/browser'

/**
 * createAxiosInstance 狀態處理說明
 *
 * 針對 401、403、409、500 狀態碼，會由此處直接處理，開發者無需額外處理。
 * 唯獨 400 狀態碼因情境較多，處理方式如下：
 *
 * 1. 若 config.metadata.userHandlesErrors 為 true，則完全交由開發者自行處理錯誤。
 * 2. 若後端回傳 ErrorMessage.Description，則優先顯示後端訊息。
 * 3. 若後端未提供錯誤訊息，則嘗試使用 config.metadata.customMsg.error 作為錯誤提示。
 * 4. 若皆無錯誤訊息，則顯示預設「請稍後再試」。
 *
 * config 格式範例（呼叫 API 時傳入）：
 * config = {
 *   metadata: { // 以下欄位皆為非必填，依需求設定
 *     userHandlesErrors: false, // 是否自定義處理錯誤，若為 true 則不由 instance 處理
 *     customMsg: {
 *       success: '', // API 成功時的 toast 訊息
 *       error: ''    // API 回 400 時的 toast 訊息
 *     }
 *   }
 * }
 *
 * 若希望任何狀態碼均自行處理錯誤，請於 config 設定：
 * metadata: {
 *   handleErrorManually: true
 * }
 */

/**
 * 建立一個自訂的 Axios 實例，並設定預設的請求與回應攔截器。
 *
 * @function
 * @param {string} baseURL - API 的基礎 URL。
 * @param {string[]} [authModeList=['Authorization', 'Kingnetauth']] - 權限驗證標頭清單，預設包含 'Authorization' 與 'Kingnetauth'。
 * @returns {import('axios').AxiosInstance} 已設定攔截器的 Axios 實例。
 *
 * @description
 * 此函式會建立一個 Axios 實例，並自動處理：
 * - 參數序列化（陣列格式為 repeat）
 * - 請求時自動帶入驗證標頭（如 Authorization、Kingnetauth）
 * - 設定 X-Correlation-Id 追蹤 ID
 * - 請求與回應的時間統計
 * - 根據回應狀態碼（401、403、409、500 等）自動彈出對應的提示訊息或導向登入頁
 * - 支援自定義錯誤訊息與成功訊息
 *
 * @example
 * const axiosInstance = createAxiosInstance('/api');
 * axiosInstance.get('/user');
 */
const createAxiosInstance = (baseURL, authModeList = ['Authorization', 'Kingnetauth']) => {
  const instance = axios.create({
    baseURL
  })
  instance.defaults.paramsSerializer = (params) => {
    return qs.stringify(params, { arrayFormat: 'repeat' })
  }
  // 避免重複出現 Alert
  const genAlertIfNotExist = (msg, finallyCallback) => {
    // eslint-disable-next-line eqeqeq
    if (document.getElementsByClassName('v-modal').length == 0) {
      Vue.prototype
        .$alert(msg, '警告', {
          confirmButtonText: i18n.t('General.Confirm'),
          type: 'warning',
          showClose: false,
          roundButton: true,
          closeOnClickModal: false
        }).finally(() => {
          finallyCallback()
        })
    }
  }
  // 401 重新登入
  const alertUnauthorized = () => {
    Vue.prototype.$logoutCommunity({
      mode: 'silent'
    })
    genAlertIfNotExist(i18n.t('Auth.Unauthorized'), () => {
      router.push({ name: 'LoginMain' })
    })
  }
  // 403
  const handle403 = (errorCode) => {
    let alertMessage = ''
    switch (errorCode) {
      case 403002:
        alertMessage = i18n.t('Auth.OldAccountNoPermission')
        break
      case 403001:
        alertMessage = i18n.t('Auth.UserNoPermission')
        break
      default:
        alertMessage = i18n.t('Auth.Forbidden')
        break
    }
    Vue.prototype.$alert(alertMessage, i18n.t('General.AlertTitle'), {
      confirmButtonText: i18n.t('General.Confirm'),
      type: 'warning',
      showClose: false,
      roundButton: true,
      closeOnClickModal: false
    }).then(() => {
      // 回首頁
      Vue.prototype.$toMainPage()
    })
  }
  // 403 非最高管理者
  const alertNotHighestAdmin = () => {
    genAlertIfNotExist(i18n.t('Auth.NotHighestAdmin'))
  }
  // 409 為資料寫入衝突，應過一陣子就可以了
  const alertTryAgainLater = (errorMessage = i18n.t('Server.TryAgainLater')) => {
    Vue.prototype.$message.error(errorMessage)
  }
  // 500
  const alertServerError = (errorMessage = i18n.t('Server.ServerError')) => {
    Vue.prototype.$message.error(errorMessage)
  }
  instance.interceptors.request.use(
    (config) => {
      const newConfig = { ...config }
      if (!checkRouteIsNoNeedToLogin() && !checkIsLoginValid()) {
        // 需要登入且驗證失敗則重新整理、將出現阻擋 Alert，且於確認後重新導向至登入頁面
        location.reload()
      }
      let webCookie = null
      try {
        webCookie = Vue.prototype.$getCookie('comLoginToken').split('token=').pop()
      } catch (error) {
        console.log(error)
      }

      // 若沒有 webCookie 且 API 需要登入進社區，則取消這筆請求
      if (!webCookie && newConfig.metadata.isApiNoNeedToLoginCommunity !== true) {
        const error = new Error('請求已取消：缺少認證 Cookie')
        error.code = 'CANCELLED_NO_COOKIE'
        return Promise.reject(error)
      }
      const xCorrelationId = Vue.prototype.$getGuid()
      const defaultHeaders = {
        'X-Correlation-Id': xCorrelationId
      }
      authModeList.forEach(key => {
        switch (key) {
          case 'Authorization':
            defaultHeaders[key] = webCookie
            break
          case 'Kingnetauth':
            defaultHeaders[key] = `Cookies ${webCookie}`
            break
          default:
            break
        }
      })
      newConfig.headers = {
        // 預設資料
        ...defaultHeaders,
        // 客製資料
        // 若有客製資料則以客製資料覆寫預設資料，以避免取用到預設的 Authorization  (e.g. smartBuildingSystemAPI.js 中供廠商巡檢用的 API)
        ...newConfig.headers
      }
      Sentry.setTag('X-Correlation-Id', xCorrelationId)
      // 如果開發者有傳入自定義config，則metadata會已經存在，若沒有則需創一個新的
      if (newConfig.metadata) {
        newConfig.metadata.startTime = new Date()
      } else {
        newConfig.metadata = { startTime: new Date() }
      }
      return newConfig
    },
    (err) => {
      return Promise.reject(err)
    }
  )
  // 先照舊，之後再整合多個 Alert 的 Issue
  instance.interceptors.response.use(
    res => {
      const newRes = { ...res }
      newRes.config.metadata.endTime = new Date()
      newRes.duration =
        newRes.config.metadata.endTime - newRes.config.metadata.startTime

      // 如果有給成功訊息就跳出提醒
      if (res.config?.metadata?.customMsg?.success) {
        Vue.prototype.$message.success(res.config.metadata.customMsg.success)
      }

      return newRes
    },
    async (err) => {
      const newErr = { ...err }
      newErr.config.metadata.endTime = new Date()
      newErr.duration =
        newErr.config.metadata.endTime - newErr.config.metadata.startTime
      if (newErr && newErr.response && newErr.response.status && newErr.config.metadata.handleErrorManually !== true) {
        const { status } = newErr.response
        switch (status) {
          case 401:
            alertUnauthorized()
            break
          case 403:
            if (newErr.response.config.url.includes('SwitchNotification')) {
              alertNotHighestAdmin()
            } else {
              handle403(newErr.response?.data?.ErrorMessage?.Code)
            }
            break
          case 404:
            // 404 不做處理，交由開發者自行處理
            break
          case 409:
            alertTryAgainLater()
            break
          case 500:
            alertServerError()
            break
          default: {
            if (newErr?.config?.metadata?.userHandlesErrors) {
              break
            }

            let errMsg = ''

            // 先看看後端有沒有給錯誤訊息
            try {
              errMsg = await getApiErrorMsg(newErr)
            } catch (error) {
              console.log(error)
            }

            // 後端沒給，但若前端config有給自定義錯誤訊息，則使用
            if (!errMsg && newErr?.config?.metadata?.customMsg?.error) {
              errMsg = newErr.config.metadata.customMsg.error
            }

            if (errMsg) {
              Vue.prototype.$message.error(errMsg)
            } else {
              // 跳出籠統的「請稍後再試」文案
              alertTryAgainLater()
            }
            break
          }
        }
      }
      return Promise.reject(newErr)
    }
  )
  return instance
}

// 取得錯誤訊息
const getApiErrorMsg = async (err) => {
  try {
    // Blob不能直接取值
    if (err.response.data instanceof Blob) {
      const text = await err.response.data.text()
      const json = JSON.parse(text)
      return json.ErrorMessage.Description
    }

    return err.response.data.ErrorMessage.Description
  } catch (error) {
    console.log(error)
    return ''
  }
}

export default createAxiosInstance
